/* TopCommandRail.jsx — iter-201D.A · Global Search Bar Layout Correction.
 *
 * Wraps the operational top chrome (global command bar + utilities)
 * into a single calm rail that sits ABOVE the page content rather
 * than floating over it.
 *
 * Responsibilities:
 *   1. Publishes `--cf-topbar-h` so page wrappers can reserve space
 *      and keep their hero/title regions away from the search.
 *   2. Hosts the GlobalCommandBar pill (left) + NotificationButton
 *      (right) on xl+ widths, in a single alignment lane.
 *   3. Stays visually infrastructural — translucent backdrop with a
 *      barely-there bottom hairline, never dominant.
 *   4. Disappears below xl so mobile / tablet keep the floating
 *      command button experience (no permanent top search bar).
 *
 * What it does NOT do:
 *   - Rebrand the logo (sidebar owns logo presentation).
 *   - Add new functionality (search behaviour unchanged).
 *   - Change page structures (only reserves vertical space).
 */
import React from "react";
import { useAuth } from "../../data/authStore";
import { useLocation } from "react-router-dom";
import { isAppRoute } from "./GlobalCommandBar";
import { GlobalCommandBarButton } from "./GlobalCommandBar";
import { NotificationButton } from "./NotificationCenter";

/** Rail height in px. Exposed for tests / page padding tokens. */
export const TOP_RAIL_HEIGHT_PX = 56;

export const TopCommandRail = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();

  // The rail only mounts on authenticated app routes — public pages
  // (landing, auth, solicitor portal, client-view) keep their own
  // chrome. On non-app routes we collapse the CSS var so pages don't
  // reserve phantom whitespace at the top.
  const visible = !!user && isAppRoute(pathname);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const root = document.documentElement;
    root.style.setProperty("--cf-topbar-h", visible ? `${TOP_RAIL_HEIGHT_PX}px` : "0px");
    return () => {
      // Don't reset on unmount — the rail is the single owner of the
      // variable across the operator shell's lifetime.
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <header
      /* xl+ only: the desktop rail. Below xl the floating
       * GlobalCommandBarMobile button takes over (see App.js). */
      className="hidden xl:flex fixed top-0 z-40 items-center justify-between transition-[left] duration-300 ease-out"
      style={{
        left: "var(--cf-sidebar-w, 260px)",
        right: 0,
        height: TOP_RAIL_HEIGHT_PX,
        paddingLeft: 24,
        paddingRight: 24,
        background: "rgba(248,250,252,0.72)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
      }}
      data-testid="top-command-rail"
    >
      <GlobalCommandBarButton />
      <div className="flex items-center gap-2" data-testid="top-command-rail-utilities">
        <NotificationButton />
      </div>
    </header>
  );
};

export default TopCommandRail;
